<template>
  <div class="m-3">
    <component
      :is="slideoverComponent"
      :title="slideoverTitle"
      :visible="slideoverVisible"
      v-bind="slideoverProperties"
      @toggle-slideover="toggleSlideover"
      @refresh-order="refreshOrder"
    />

    <div class="pb-3 flex flex-row justify-end">
      <ActionsMenu v-if="canShowActions">
        <ActionsItem
          v-if="userCanAddTransaction"
          @click="toggleSlideover('AddTransactionSlideover', 'Add Transaction')"
        >
          Add Transaction
        </ActionsItem>
        <ActionsItem
          v-if="userCanEditTransaction"
          :disabled="!hasSelectedTransaction"
          @click="
            toggleSlideover('EditTransactionSlideover', 'Edit Transaction', {
              transaction: selectedTransaction,
            })
          "
        >
          Edit Transaction
        </ActionsItem>
        <ActionsItem
          v-if="userCanRefundTransaction"
          :disabled="!transactionCanBeRefunded"
          @click="
            toggleSlideover(
              'RefundTransactionSlideover',
              'Refund Transaction',
              { transaction: selectedTransaction }
            )
          "
        >
          Refund Transaction
        </ActionsItem>
      </ActionsMenu>
    </div>

    <div style="height: 200px">
      <ag-grid-vue
        style="width: 100%; height: 100%"
        class="ag-theme-balham"
        :columnDefs="columnDefs"
        :rowData="rowData"
        :pagination="false"
        id="orderTransaction"
        :gridOptions="gridOptions"
        @grid-ready="onGridReady"
        :context="context"
        :frameworkComponents="frameworkComponents"
        :defaultColDef="defaultColDef"
        :enableCellTextSelection="true"
        :ensureDomOrder="true"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
import auth from "@/api/auth";
import agGrid from "@/mixins/agGrid";
import { AgGridVue } from "ag-grid-vue";
import moneyFormatter from "@/mixins/moneyFormatter";
import { date } from "@/components/agGrid/cellRenderers";
import AddTransactionSlideover from "./slideovers/OrderEdit/AddTransaction.vue";
import EditTransactionSlideover from "./slideovers/OrderEdit/EditTransaction.vue";
import RefundTransactionSlideover from "./slideovers/OrderEdit/RefundTransaction.vue";

export default {
  name: "OrderTransactions",
  components: {
    AgGridVue,
    AddTransactionSlideover,
    EditTransactionSlideover,
    RefundTransactionSlideover,
  },
  mixins: [agGrid, moneyFormatter],
  props: {
    order: {
      type: [Object],
      required: true,
    },
  },
  data() {
    return {
      columnDefs: null,
      rowData: null,
      pagination: false,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      overlayLoadingTemplate: true,
      selectedTransaction: {},
      slideoverTitle: "",
      slideoverVisible: false,
      slideoverComponent: null,
      slideoverProperties: {},
    };
  },
  computed: {
    userCanAddTransaction() {
      if (auth.isUserAuthorized(["admin", "supervisor"])) {
        return true;
      }

      if (this.isThirdPartyOrder && auth.isUserAuthorized("third-party")) {
        return true;
      }

      return false;
    },
    userCanEditTransaction() {
      return auth.isUserAuthorized(["admin", "supervisor"]);
    },
    userCanRefundTransaction() {
      return auth.isUserAuthorized(["admin", "supervisor"]);
    },
    canShowActions() {
      if (this.order.status?.Status === "Unprocessed") {
        return false;
      }

      return (
        this.userCanAddTransaction ||
        this.userCanEditTransaction ||
        this.userCanRefundTransaction
      );
    },
    transactionCanBeRefunded() {
      if (!this.hasSelectedTransaction) {
        return false;
      }

      return this.selectedTransaction?.Amount > 0;
    },
    hasSelectedTransaction() {
      return Boolean(this.selectedTransaction.AutoNumber);
    },
    isThirdPartyOrder() {
      return [11, 24, 25, 26, 27, 29].includes(Number(this.order.CartID));
    },
  },

  beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 30,
      pagination: false,
      suppressPaginationPanel: true,
      rowSelection: "single",
      rowDeselection: true,
      onSelectionChanged: this.transactionSelectionChanged,
    };
    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: false,
      filter: false,
      flex: 1,
      suppressMovable: true,
    };

    this.columnDefs = [
      {
        headerName: "Date",
        valueGetter: (params) => new Date(params.data.Date),
        cellRenderer: "dateCell",
        width: 90,
        flex: 0,
      },
      {
        headerName: "Description",
        field: "Description",
      },
      {
        headerName: "Amount",
        field: "Amount",
        valueGetter: (params) => {
          return moneyFormatter.format(params.data.Amount);
        },
        width: 90,
        flex: 0,
      },
      {
        headerName: "Notes",
        field: "Notes",
      },
      {
        headerName: "Approval Code",
        field: "ApprovalCode",
      },
    ];

    this.frameworkComponents = {
      dateCell: date,
    };
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;

    this.$store
      .dispatch("orders/fetchTransactions", this.order.SourceOrderNumber)
      .then(() => {
        this.rowData = this.order?.transactions || [];
      });
  },
  methods: {
    onGridReady() {},
    transactionSelectionChanged() {
      let rows = this.gridOptions.api.getSelectedRows();
      if (rows.length !== 1) {
        this.selectedTransaction = {};
        return;
      }
      this.selectedTransaction = rows[0];
    },
    toggleSlideover(component, title, properties = {}) {
      if (component) {
        this.slideoverComponent = component;
        this.slideoverTitle = title;
        this.slideoverProperties = properties;
      }

      setTimeout(() => {
        this.slideoverVisible = !this.slideoverVisible;
      }, 3);
    },
    refreshOrder() {
      this.$router.go(0);
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
